// RolesManager.jsx - With permission-based button visibility
import React, { useState, useEffect, useMemo } from 'react';
import { Table, Button, Modal, Spinner, Form, Badge } from 'react-bootstrap';
import { 
  Shield, 
  Search, 
  Plus, 
  Edit2, 
  Trash2, 
  Users, 
  X,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  AlertTriangle,
  Lock
} from 'lucide-react';
import { roleService, roleManagementService } from '../../services';
import { useAuth } from '../../context';
import { PERMISSIONS } from '../../constants';
import './RolesManager.css';

const RolesManager = () => {
  const { hasPermission } = useAuth();

  // ✅ Permission checks for buttons
  const canCreate = hasPermission(PERMISSIONS.ROLES.CREATE);
  const canEdit = hasPermission(PERMISSIONS.ROLES.EDIT);
  const canDelete = hasPermission(PERMISSIONS.ROLES.DELETE);

  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [submitting, setSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    loadRoles();
  }, []);

  const loadRoles = async () => {
    setLoading(true);
    setError('');
    const result = await roleService.getRoles();
    if (result.success) {
      setRoles(result.data || []);
    } else {
      setError(result.message || 'Failed to load roles');
    }
    setLoading(false);
  };

  const filteredRoles = useMemo(() => {
    if (!searchTerm.trim()) return roles;
    return roles.filter(role =>
      role.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      role.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [roles, searchTerm]);

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) {
      errors.name = 'Role name is required';
    }
    if (formData.name.trim() && roles.some(r => 
      r.name?.toLowerCase() === formData.name.trim().toLowerCase() && 
      r.uuid !== selectedRole?.uuid
    )) {
      errors.name = 'Role name already exists';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreate = async () => {
    if (!validateForm()) return;
    
    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const result = await roleManagementService.createRole(
        formData.name.trim(),
        formData.description.trim()
      );
      
      if (result.success) {
        setSuccess(result.message || 'Role created successfully');
        await loadRoles();
        setShowCreateModal(false);
        setFormData({ name: '', description: '' });
        setTimeout(() => setSuccess(''), 4000);
      } else {
        setError(result.message || 'Failed to create role');
      }
    } catch (err) {
      setError(err.message || 'An unexpected error occurred');
    }
    setSubmitting(false);
  };

  const handleUpdate = async () => {
    if (!validateForm()) return;
    if (!selectedRole) return;
    
    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const result = await roleManagementService.updateRole(
        selectedRole.uuid,
        formData.name.trim(),
        formData.description.trim()
      );
      
      if (result.success) {
        setSuccess(result.message || 'Role updated successfully');
        await loadRoles();
        setShowEditModal(false);
        setSelectedRole(null);
        setFormData({ name: '', description: '' });
        setTimeout(() => setSuccess(''), 4000);
      } else {
        setError(result.message || 'Failed to update role');
      }
    } catch (err) {
      setError(err.message || 'An unexpected error occurred');
    }
    setSubmitting(false);
  };

  const handleDelete = async () => {
    if (!selectedRole) return;
    
    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const result = await roleManagementService.deleteRole(selectedRole.uuid);
      
      if (result.success) {
        setSuccess(result.message || 'Role deleted successfully');
        await loadRoles();
        setShowDeleteModal(false);
        setSelectedRole(null);
        setTimeout(() => setSuccess(''), 4000);
      } else {
        setError(result.message || 'Failed to delete role');
      }
    } catch (err) {
      setError(err.message || 'An unexpected error occurred');
    }
    setSubmitting(false);
  };

  const handleEditClick = (role) => {
    setSelectedRole(role);
    setFormData({ 
      name: role.name || '', 
      description: role.description || '' 
    });
    setFormErrors({});
    setShowEditModal(true);
  };

  const handleDeleteClick = (role) => {
    setSelectedRole(role);
    setShowDeleteModal(true);
  };

  const getStatusConfig = (role) => {
    const isActive = role.is_active !== undefined ? role.is_active : true;
    return isActive 
      ? { color: '#10B981', bg: '#D1FAE5', label: 'Active', icon: CheckCircle }
      : { color: '#94A3B8', bg: '#F1F5F9', label: 'Inactive', icon: AlertCircle };
  };

  const getRoleBadge = (roleName) => {
    const styles = {
      super_admin: { background: '#EDE9FE', color: '#4C1D95' },
      company_admin: { background: '#DBEAFE', color: '#1E3A8A' },
      club_admin: { background: '#DBEAFE', color: '#1E3A8A' },
      club_staff: { background: '#E0F2FE', color: '#0369A1' },
      club_viewer: { background: '#F1F5F9', color: '#64748B' }
    };
    return styles[roleName?.toLowerCase()] || { background: '#F1F5F9', color: '#64748B' };
  };

  return (
    <div className="roles-manager">
      {/* Alerts */}
      {success && (
        <div className="roles-manager-success">
          <CheckCircle size={18} />
          {success}
        </div>
      )}

      {error && (
        <div className="roles-manager-error">
          <AlertCircle size={18} />
          {error}
        </div>
      )}

      {/* Toolbar */}
      <div className="roles-manager-toolbar">
        <div className="roles-manager-search">
          <Search size={18} className="roles-manager-search-icon" />
          <input
            type="text"
            placeholder="Search roles by name or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="roles-manager-search-input"
            disabled={loading}
          />
          {searchTerm && (
            <button 
              className="roles-manager-search-clear"
              onClick={() => setSearchTerm('')}
            >
              <X size={16} />
            </button>
          )}
        </div>
        <div className="roles-manager-actions">
          <button onClick={loadRoles} className="roles-manager-refresh-btn">
            <RefreshCw size={16} className={loading ? 'spinning' : ''} />
            Refresh
          </button>
          {/* ✅ Only show Create button if user has roles.create */}
          {canCreate && (
            <button 
              className="roles-manager-create-btn"
              onClick={() => {
                setFormData({ name: '', description: '' });
                setFormErrors({});
                setShowCreateModal(true);
              }}
            >
              <Plus size={18} />
              Create Role
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="roles-manager-loading">
          <Spinner animation="border" />
          <p>Loading roles...</p>
        </div>
      ) : filteredRoles.length === 0 ? (
        <div className="roles-manager-empty">
          <Shield size={48} />
          <p className="roles-manager-empty-title">No roles found</p>
          <p className="roles-manager-empty-subtitle">
            {searchTerm ? 'Try adjusting your search' : 'Create your first role'}
          </p>
        </div>
      ) : (
        <div className="roles-manager-table-wrapper">
          <Table hover className="roles-manager-table">
            <thead>
              <tr className="roles-manager-table-header">
                <th>Role</th>
                <th>Description</th>
                <th>Users</th>
                <th>Status</th>
                <th>Created</th>
                {/* ✅ Only show Actions column if user can edit or delete */}
                {(canEdit || canDelete) && (
                  <th className="roles-manager-actions-header">Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {filteredRoles.map((role) => {
                const statusConfig = getStatusConfig(role);
                const StatusIcon = statusConfig.icon;
                const roleBadge = getRoleBadge(role.name);
                return (
                  <tr key={role.uuid || role.id} className="roles-manager-table-row">
                    <td>
                      <div className="roles-manager-role-cell">
                        <div className="roles-manager-role-icon" style={{ 
                          background: roleBadge.background, 
                          color: roleBadge.color 
                        }}>
                          <Shield size={16} />
                        </div>
                        <div>
                          <span className="roles-manager-role-name">
                            {role.name?.replace('_', ' ') || 'Unknown'}
                          </span>
                          {role.is_system && (
                            <span className="roles-manager-system-badge">
                              <Lock size={12} />
                              System
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="roles-manager-role-description">
                      {role.description || '—'}
                    </td>
                    <td>
                      <span className="roles-manager-user-count">
                        {role.user_count || 0}
                      </span>
                    </td>
                    <td>
                      <span className="roles-manager-status-badge" style={{ 
                        background: statusConfig.bg, 
                        color: statusConfig.color 
                      }}>
                        <StatusIcon size={12} />
                        {statusConfig.label}
                      </span>
                    </td>
                    <td className="roles-manager-created-at">
                      {role.created_at ? new Date(role.created_at).toLocaleDateString() : '—'}
                    </td>
                    {/* ✅ Only show Actions cell if user can edit or delete */}
                    {(canEdit || canDelete) && (
                      <td className="roles-manager-actions-cell">
                        <div className="roles-manager-actions-wrapper">
                          {/* ✅ Edit button only if canEdit */}
                          {canEdit && (
                            <button
                              className="roles-manager-action-btn roles-manager-action-edit"
                              onClick={() => handleEditClick(role)}
                              disabled={role.is_system}
                              title={role.is_system ? 'System roles cannot be edited' : 'Edit role'}
                            >
                              <Edit2 size={16} />
                            </button>
                          )}
                          {/* ✅ Delete button only if canDelete */}
                          {canDelete && (
                            <button
                              className="roles-manager-action-btn roles-manager-action-delete"
                              onClick={() => handleDeleteClick(role)}
                              disabled={role.is_system || (role.user_count > 0)}
                              title={
                                role.is_system 
                                  ? 'System roles cannot be deleted' 
                                  : role.user_count > 0 
                                    ? 'Cannot delete role with assigned users' 
                                    : 'Delete role'
                              }
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      )}

      {/* Create Modal */}
      <Modal 
        show={showCreateModal} 
        onHide={() => setShowCreateModal(false)}
        centered
        className="roles-manager-modal"
      >
        <Modal.Header className="roles-manager-modal-header">
          <Modal.Title className="roles-manager-modal-title">
            <Plus size={20} className="roles-manager-modal-icon" />
            Create New Role
          </Modal.Title>
          <button 
            className="roles-manager-modal-close"
            onClick={() => setShowCreateModal(false)}
          >
            <X size={20} />
          </button>
        </Modal.Header>
        <Modal.Body className="roles-manager-modal-body">
          <Form>
            <Form.Group className="roles-manager-form-group">
              <Form.Label className="roles-manager-form-label">
                Role Name <span className="roles-manager-required">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter role name..."
                value={formData.name}
                onChange={(e) => {
                  setFormData({ ...formData, name: e.target.value });
                  if (formErrors.name) setFormErrors({ ...formErrors, name: '' });
                }}
                className={`roles-manager-form-input ${formErrors.name ? 'roles-manager-form-input-error' : ''}`}
                isInvalid={!!formErrors.name}
              />
              {formErrors.name && (
                <Form.Text className="roles-manager-form-error">
                  {formErrors.name}
                </Form.Text>
              )}
            </Form.Group>

            <Form.Group className="roles-manager-form-group">
              <Form.Label className="roles-manager-form-label">
                Description
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Enter role description..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="roles-manager-form-textarea"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className="roles-manager-modal-footer">
          <Button 
            variant="secondary" 
            onClick={() => setShowCreateModal(false)}
            className="roles-manager-modal-cancel"
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleCreate}
            className="roles-manager-modal-save"
            disabled={submitting}
          >
            {submitting ? (
              <>
                <Spinner animation="border" size="sm" />
                Creating...
              </>
            ) : (
              'Create Role'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Modal */}
      <Modal 
        show={showEditModal} 
        onHide={() => setShowEditModal(false)}
        centered
        className="roles-manager-modal"
      >
        <Modal.Header className="roles-manager-modal-header">
          <Modal.Title className="roles-manager-modal-title">
            <Edit2 size={20} className="roles-manager-modal-icon" />
            Edit Role
          </Modal.Title>
          <button 
            className="roles-manager-modal-close"
            onClick={() => setShowEditModal(false)}
          >
            <X size={20} />
          </button>
        </Modal.Header>
        <Modal.Body className="roles-manager-modal-body">
          <Form>
            <Form.Group className="roles-manager-form-group">
              <Form.Label className="roles-manager-form-label">
                Role Name <span className="roles-manager-required">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter role name..."
                value={formData.name}
                onChange={(e) => {
                  setFormData({ ...formData, name: e.target.value });
                  if (formErrors.name) setFormErrors({ ...formErrors, name: '' });
                }}
                className={`roles-manager-form-input ${formErrors.name ? 'roles-manager-form-input-error' : ''}`}
                isInvalid={!!formErrors.name}
              />
              {formErrors.name && (
                <Form.Text className="roles-manager-form-error">
                  {formErrors.name}
                </Form.Text>
              )}
            </Form.Group>

            <Form.Group className="roles-manager-form-group">
              <Form.Label className="roles-manager-form-label">
                Description
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Enter role description..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="roles-manager-form-textarea"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className="roles-manager-modal-footer">
          <Button 
            variant="secondary" 
            onClick={() => setShowEditModal(false)}
            className="roles-manager-modal-cancel"
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleUpdate}
            className="roles-manager-modal-save"
            disabled={submitting}
          >
            {submitting ? (
              <>
                <Spinner animation="border" size="sm" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Modal */}
      <Modal 
        show={showDeleteModal} 
        onHide={() => setShowDeleteModal(false)}
        centered
        className="roles-manager-modal"
      >
        <Modal.Header className="roles-manager-modal-header">
          <Modal.Title className="roles-manager-modal-title">
            <AlertTriangle size={20} className="roles-manager-modal-icon-danger" />
            Delete Role?
          </Modal.Title>
          <button 
            className="roles-manager-modal-close"
            onClick={() => setShowDeleteModal(false)}
          >
            <X size={20} />
          </button>
        </Modal.Header>
        <Modal.Body className="roles-manager-modal-body">
          <p className="roles-manager-delete-message">
            Are you sure you want to delete <strong>'{selectedRole?.name}'</strong>? 
            This action cannot be undone.
          </p>
          {selectedRole?.user_count > 0 && (
            <div className="roles-manager-delete-warning">
              <AlertTriangle size={16} />
              <span>
                This role is assigned to <strong>{selectedRole.user_count} user{selectedRole.user_count !== 1 ? 's' : ''}</strong>. 
                Cannot delete until users are reassigned.
              </span>
            </div>
          )}
          {selectedRole?.is_system && (
            <div className="roles-manager-delete-warning">
              <AlertTriangle size={16} />
              <span>
                This is a <strong>System Role</strong> and cannot be deleted.
              </span>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="roles-manager-modal-footer">
          <Button 
            variant="secondary" 
            onClick={() => setShowDeleteModal(false)}
            className="roles-manager-modal-cancel"
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={handleDelete}
            className="roles-manager-modal-delete"
            disabled={submitting || selectedRole?.user_count > 0 || selectedRole?.is_system}
          >
            {submitting ? (
              <>
                <Spinner animation="border" size="sm" />
                Deleting...
              </>
            ) : (
              'Delete Role'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default RolesManager;